// Лёгкий persistent-снимок последних сообщений чата в localStorage.
//
// Зачем: при холодном старте / полной перезагрузке PWA (частый iOS-кейс) экран чата
// раньше стартовал пустым со спиннером и грузил историю с нуля — «флап». Снимок даёт
// мгновенную гидрацию: показываем закэшированные сообщения сразу, а сеть тихо домержит
// свежую версию (reconcileLatestMessages) без сброса списка.
//
// Что храним: только лёгкие поля. Картинки data URL (imageDataUrl/imageDataUrls, base64
// по сотни КБ) вырезаем — они упрут 5МБ-квоту localStorage. Этап 31: ссылки на картинки
// (imageUrls) — короткие строки, их храним: гидрированная лента сразу показывает фото
// из кэша браузера.

import { messageImages } from './message-images.js';

const CACHE_KEY_PREFIX = 'petrovich:msgcache:';
// Снимок «хвоста» диалога для мгновенной гидрации, а не полная история (её дотянет сеть).
const MAX_CACHED_MESSAGES = 80;

function hasStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch (_) {
    return false;
  }
}

function isQuotaError(error) {
  if (!error) {
    return false;
  }
  if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
    return true;
  }
  return typeof error.code === 'number' && (error.code === 22 || error.code === 1014);
}

// Оставляем только лёгкие поля, совпадающие с идентичностью сообщения (role:requestId
// или id) — этого достаточно для рендера и для последующего неразрушающего merge.
function stripMessage(message) {
  const imageCount = messageImages(message).length || message.imageCount || 0;
  const imageUrls = Array.isArray(message.imageUrls) && message.imageUrls.length ? message.imageUrls : null;
  return {
    id: message.id,
    role: message.role,
    text: message.text || '',
    ts: message.ts || null,
    requestId: message.requestId || null,
    model: message.model || null,
    // К5 (1.27.7): сами картинки не храним, но число помним — недоставленное
    // сообщение с фото после перезагрузки честно скажет «Фото не сохранилось».
    ...(imageCount ? { imageCount } : {}),
    ...(imageUrls ? { imageUrls } : {}),
  };
}

function cacheKey(chatId) {
  return `${CACHE_KEY_PREFIX}${chatId}`;
}

function removeKey(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (_) {
    // best effort
  }
}

// Синхронно читает снимок чата (для гидрации ДО первого рендера, без флэша спиннера).
export function readSnapshot(chatId) {
  if (!hasStorage() || !chatId) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(cacheKey(chatId));
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (item) => item && typeof item.id === 'string' && typeof item.role === 'string'
    );
  } catch (_) {
    return [];
  }
}

// Пишет снимок «хвоста» диалога. best-effort: при переполнении квоты не бросает, а
// удаляет ключ и возвращает false (снимок не критичен — просто не будет гидрации).
// После 401 запись снимков запечатана до следующего успешного запроса: экран чата
// размонтируется ПОЗЖЕ очистки (onDestroy сохраняет хвост) и без печати возвращал
// переписку на диск — а войти могли уже под другим пользователем.
let writesSealed = false;

export function sealWrites() {
  writesSealed = true;
}

export function unsealWrites() {
  writesSealed = false;
}

export function writeSnapshot(chatId, messages) {
  if (!hasStorage() || !chatId || writesSealed) {
    return true;
  }
  const key = cacheKey(chatId);

  const tail = Array.isArray(messages)
    ? messages.filter((message) => message && !message._streaming).slice(-MAX_CACHED_MESSAGES)
    : [];

  if (tail.length === 0) {
    removeKey(key);
    return true;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(tail.map(stripMessage)));
    return true;
  } catch (error) {
    removeKey(key);
    if (isQuotaError(error)) {
      return false;
    }
    throw error;
  }
}

// Удаляет снимок одного чата (например, при удалении чата).
export function clearSnapshot(chatId) {
  if (!hasStorage() || !chatId) {
    return;
  }
  removeKey(cacheKey(chatId));
}

// Удаляет все снимки сообщений (при выходе/401 — сообщения не должны оставаться на диске).
export function clearAll() {
  if (!hasStorage()) {
    return;
  }
  try {
    const keys = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach(removeKey);
  } catch (_) {
    // best effort
  }
}
